import {
  CloseCircleOutlined,
  CloseOutlined,
  MinusCircleOutlined
} from "@ant-design/icons";
import { Spin, Tree, Typography } from "antd";
import Search from "antd/lib/input/Search";
import React from "react";
import { TTranslate } from "../../../../core/service/locale/TranslationService";
import { TOrganisationIndexTreeItem } from "../../../../organisations/presenter/model/OrganisationIndexTreePresentationModel";
import { TFacet } from "../../../model/SearchResponse";

const { Text } = Typography;
export interface Props {
  translate: TTranslate;
  indexTrees: TOrganisationIndexTreeItem[][];
  onCheck(checkedItems: string[]): void;
  facet?: TFacet;
  loading?: boolean;
  showFacetCount?: boolean;
  selectedIndexTypes?: string[];
  searchVisible?: boolean;
  checkable?: boolean;
  selectable?: boolean;
  onSelect?(keys: any[], info: any): void;
  selectedKeys?: string[];
}

export interface State {
  checkedItems: string[][];
  searchFilter: string;
  expandedKeys?: string[];
  autoExpandParent: boolean;
}

function flatDeep(arr, d = 1) {
  return d > 0
    ? arr.reduce(
        (acc, val) =>
          acc.concat(Array.isArray(val) ? flatDeep(val, d - 1) : val),
        []
      )
    : arr.slice();
}

function renderTreeNodeTitle(node, searchFilter, showFacetCount) {
  return (
    <TreeNodeTitle
      node={node}
      searchFilter={searchFilter}
      showFacetCount={showFacetCount}
    />
  );
}

export default class IndexSelectionTree extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      checkedItems: [],
      searchFilter: "",
      expandedKeys: [],
      autoExpandParent: true
    };
    this.onUpdateCheckedItems = this.onUpdateCheckedItems.bind(this);
    this.onExpand = this.onExpand.bind(this);
    this.onSearch = this.onSearch.bind(this);
  }

  onExpand(expandedKeys, filteredData) {
    this.setState({
      expandedKeys,
      autoExpandParent: true
    });
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.expandedKeys === undefined && this.props.facet) {
      this.setState({
        expandedKeys: Object.keys(this.props.facet || {}) || []
      });
    }
    if (
      prevProps.selectedIndexTypes !== undefined &&
      this.state.checkedItems?.length === 0
    ) {
      if (this.props.selectedIndexTypes !== undefined) {
        this.setState({
          checkedItems: Array.isArray(this.props.selectedIndexTypes)
            ? this.props.selectedIndexTypes.map((item) => [item])
            : [(this.props.selectedIndexTypes as any).toString()]
        });
      }
    }
  }

  onUpdateCheckedItems(index, checked, info) {
    let list = Array.isArray(checked) ? checked : checked.checked;
    let checkedItems = this.state.checkedItems;
    checkedItems[index] = list;
    this.setCheckedItems(checkedItems);
  }

  setCheckedItems(items) {
    let checkedItems = items;
    this.setState(
      {
        checkedItems
      },
      () => {
        let total = checkedItems.reduce((prev, next) => {
          return [...prev, ...next];
        }, []);
        this.props.onCheck(total.map((item) => item.toString()));
      }
    );
  }

  onSearch(value: string) {
    this.setState({
      searchFilter: value
    });
  }
  render() {
    const {
      indexTrees,
      loading,
      translate,
      showFacetCount = false,
      searchVisible = true,
      checkable = true,
      selectable = false,
      onSelect,
      selectedKeys
    } = this.props;
    return (
      <div style={{ minHeight: 70, marginLeft: -5 }}>
        {searchVisible ? (
          <Search
            style={{ marginBottom: 8, paddingLeft: 5 }}
            allowClear
            placeholder={translate("data_grid.search")}
            onChange={(event) => {
              this.onSearch(event.target.value);
            }}
            suffix={
              this.state.expandedKeys?.length ? (
                <MinusCircleOutlined
                  style={{
                    color: "rgba(0, 23, 31, 0.45)"
                  }}
                  onClick={() => {
                    this.setState({
                      expandedKeys: [],
                      checkedItems: []
                    });
                    this.props.onCheck([]);
                  }}
                />
              ) : null
            }
          />
        ) : null}
        <Spin
          spinning={loading}
          style={{
            height: "100%",
            marginTop: loading ? 20 : 0
          }}
        >
          {indexTrees?.map((tree, index) => {
            let filteredData = tree;
            let expandedKeys = this.state.expandedKeys || [];
            let searchCriteria = this.state.searchFilter?.toLowerCase() || "";
            if (searchCriteria?.length > 2) {
              filteredData = tree
                .map((item) => item.withFilter(searchCriteria || undefined))
                .filter((item) => {
                  let childrenHasValue = item.children.some((child) =>
                    child.item.contains(searchCriteria)
                  );

                  return (
                    childrenHasValue ||
                    item.title.toLowerCase().indexOf(searchCriteria) !== -1
                  );
                });
              expandedKeys = [
                ...expandedKeys,
                ...flatDeep(
                  filteredData.flatMap((data) => data.collectIds()),
                  Infinity
                )
              ];
            } else {
              filteredData = tree.map((item) =>
                item.withFilter(searchCriteria || undefined)
              );
            }

            if (searchCriteria.length > 2 && filteredData.length === 0) {
              return (
                <div
                  key={`no-data`}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <Text type="secondary" style={{ textAlign: "center" }}>
                    {translate("data_grid.empty_text")}
                  </Text>
                </div>
              );
            }
            const selectionProps = selectable
              ? {
                  selectedKeys
                }
              : {
                  expandedKeys: expandedKeys,
                  onExpand: (keys) => this.onExpand(keys, filteredData),
                  autoExpandParent:
                    this.state.searchFilter?.length > 2 || selectable
                };
            return (
              <Tree
                checkedKeys={this.state.checkedItems[index]}
                treeData={filteredData}
                key={`tree-${index}`}
                checkable={checkable}
                selectable={selectable}
                defaultCheckedKeys={this.props.selectedIndexTypes}
                checkStrictly
                titleRender={(node) =>
                  renderTreeNodeTitle(
                    node,
                    this.state.searchFilter,
                    showFacetCount
                  )
                }
                className={"search-sider-tree"}
                onCheck={(checked, info) => {
                  this.onUpdateCheckedItems(index, checked, info);
                }}
                onSelect={(keys, info) => {
                  if (selectable) {
                    onSelect?.(keys, info);
                  }
                }}
                {...selectionProps}
              />
            );
          })}
        </Spin>
      </div>
    );
  }
}

const TreeNodeTitle = ({ node, showFacetCount, searchFilter }) => {
  let count = (node as any)._count || 0;
  const searchValue = searchFilter;
  let title: string = (node as any).title;
  const idx = searchValue
    ? title.toLowerCase().indexOf(searchValue.toLowerCase())
    : -1;
  const beforeStr = title.substr(0, idx);
  const afterStr = title.substr(idx + searchValue.length);
  let label = `${idx > -1 ? afterStr : node.title}`;
  if (showFacetCount) {
    label += ` (${count})`;
  }
  return idx > -1 ? (
    <span style={count ? { fontWeight: 700 } : {}}>
      {beforeStr}
      <mark>{title.substr(idx, searchValue.length)}</mark>
      {label}
    </span>
  ) : (
    <span style={count ? { fontWeight: 700 } : {}}>{label}</span>
  );
};
